import { hasUtmParams, readUtmParams, UTM_STORAGE_KEY, type UtmParams } from '@/lib/utm-attribution'

const VISITOR_KEY = '_atrium_vid'
const SESSION_KEY = '_atrium_sid'

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return
  const maxAge = 60 * 60 * 24 * 365 * 2 // 2 years
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAge}; path=/; SameSite=Lax`
}

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

function getVisitorId(): string {
  let vid = getCookie(VISITOR_KEY)
  if (!vid) {
    vid = generateUUID()
    setCookie(VISITOR_KEY, vid)
  }
  return vid
}

function getSessionId(): string | null {
  return getCookie(SESSION_KEY)
}

function getStoredUtmParams(): UtmParams {
  if (typeof sessionStorage === 'undefined') return {}
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function getUtmParams(): UtmParams {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return {}

  const currentParams = readUtmParams(new URLSearchParams(window.location.search))
  if (hasUtmParams(currentParams)) {
    try {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(currentParams))
    } catch {
      // Storage is best-effort; still attach the params to this event.
    }
    return currentParams
  }

  return getStoredUtmParams()
}

export function emitFunnelEvent(
  eventName: string,
  category: string,
  properties?: Record<string, unknown>,
): void {
  const utm = getUtmParams()

  fetch('/api/funnel-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event_name: eventName,
      event_category: category,
      event_properties: properties ?? {},
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      utm_source: utm.utm_source,
      utm_medium: utm.utm_medium,
      utm_campaign: utm.utm_campaign,
      utm_term: utm.utm_term,
      utm_content: utm.utm_content,
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      landing_page: typeof window !== 'undefined' ? window.location.pathname : undefined,
    }),
  }).catch(() => {})
}
